import { ScrollView, StyleProp, ViewStyle, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useCallback, useRef, useEffect } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { useSettingsData } from "../../../contexts/SettingsDataContext";
import SettingsSkeleton from "./common/settings-skeleton";

interface SettingsTabPageProps {
  children: React.ReactNode;
  className?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  section?: string;
}

const SettingsTabPage = ({
  children,
  className,
  contentContainerStyle,
  section,
}: SettingsTabPageProps) => {
  const { isLoading } = useSettingsData();
  const scrollViewRef = useRef<ScrollView>(null);
  const router = useRouter();

  useFocusEffect(
    useCallback(() => {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false });
    }, [])
  );

  // scroll to anonymous calls setting
  useEffect(() => {
    if (section && typeof section === "string" && section === "call-types") {
      const timer = setTimeout(() => {
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollToEnd({ animated: true });
          setTimeout(() => {
            router.setParams({ section: undefined });
          }, 500);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [section, router]);

  const handleScroll = useCallback((event: any) => {
    const customEvent = new CustomEvent("settingsScroll", {
      detail: event,
    });
    document.dispatchEvent(customEvent);
  }, []);

  if (isLoading) {
    return <SettingsSkeleton />;
  }

  return (
    <SafeAreaView className={`flex-1 bg-gray-50 ${className}`}>
      <ScrollView
        ref={scrollViewRef}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[contentContainerStyle, { paddingTop: 53 }]}
        bounces={false}
        alwaysBounceVertical={false}
      >
        <View className="flex-1 w-full max-w-2xl mx-auto p-1 sm:p-6 pb-40 sm:pb-20">
          {children}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default SettingsTabPage;
